<template>
	<div
		class="won-background-dark flex h-[60px] w-full items-center justify-between pr-5 pl-6"
	>
		<div class="flex items-center">
			<div
				class="won-background-dark-200 mr-6 flex size-8 cursor-pointer items-center justify-center rounded-md"
				@click.prevent="handleBack"
			>
				<arrow-back />
			</div>

			<input
				ref="resumeInput"
				v-model="title"
				class="pdf-input h-8 w-fit min-w-[260px] px-2"
				@keypress.enter="(el) => el.target.blur()"
				@blur.stop="handleTitle"
				@focus="selectContent"        
			/>

			<saved-resume
				v-show="saved"
				class="ml-4"
			/>
		</div>

		<!--
			EN-806 tutorial anchor: with EN-808 now merged in, this wraps the header's actual
			"main actions" cluster — the Save & Continue action plus the "..." menu holding
			Share/Download further down. EN-806's "Updated Main Actions" step points at it to
			spotlight what changed. See the step definition in modals/resume-score-whats-new.vue.
		-->
		<div
			id="onboarding-header-actions-anchor"
			class="flex items-center gap-2"
		>
			<router-link
				:to="{ name: 'job-board-search' }"
				class="
					flex h-8 items-center justify-center rounded bg-won-green px-3
					text-sm font-medium text-white select-none
				"
			>
				Save &amp; Continue
			</router-link>


			<Menu
				id="onboarding-builder-download"
				as="div"
				data-step="8"
				class="resume-settings relative inline-block text-left"
			>
				<div>
					<MenuButton
						class="
							flex size-8 cursor-pointer items-center justify-center rounded
							bg-[#b6dbc4] px-2 py-2.5 select-none
						"
					>
						<svg
							class="size-4 stroke-2 text-won-blue"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						> <g clip-path="url(#clip0_201_11008)"> <path
							d="M12 8C13.1 8 14 7.1 14 6C14 4.9 13.1 4 12 4C10.9 4 10 4.9 10 6C10 7.1 10.9 8 12 8ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10ZM12 16C10.9 16 10 16.9 10 18C10 19.1 10.9 20 12 20C13.1 20 14 19.1 14 18C14 16.9 13.1 16 12 16Z"
							fill="#073B5D"
						/> </g> <defs> <clipPath id="clip0_201_11008"> <rect
							width="24"
							height="24"
							fill="white"
						/> </clipPath> </defs> </svg>
					</MenuButton>
				</div>

				<transition
					enter-active-class="transition ease-out duration-100"
					enter-from-class="transform opacity-0 scale-95"
					enter-to-class="transform opacity-100 scale-100"
					leave-active-class="transition ease-in duration-75"
					leave-from-class="transform opacity-100 scale-100"
					leave-to-class="transform opacity-0 scale-95"
				>
					<MenuItems
						:static="isOnboarding && onboardingStep === 'onboarding-builder-download'"
						class="
							absolute right-0 z-10 mt-2 w-[168px] origin-top-right rounded
							bg-white shadow-lg ring-1 ring-black/5
							focus:outline-hidden
						"
					>
						<div class="py-1">
							<MenuItem
								v-if="coverLetter === false && userHaveAccessTo('resume-share')"
								v-slot="{ active }"
							>
								<a
									:class="[active ? 'bg-won-sand-light' : '']"
									class="
										flex cursor-pointer items-center justify-start px-2 py-1
										text-[12px] leading-[14px] font-normal text-won-blue
									"
									@click="handleShare"
								>
									<UserGroupIcon class="mr-1 size-4 text-won-blue" />
									Share
								</a>
							</MenuItem>
							<MenuItem
								v-slot="{ active }"
								:disabled="loadingDownload"
							>
								<a
									:class="[
										active ? 'bg-won-sand-light' : '',
										loadingDownload ? 'cursor-not-allowed opacity-50' : ''
									]"
									class="
										flex cursor-pointer items-center justify-start px-2 py-1
										text-[12px] leading-[14px] font-normal text-won-blue
									"
									@click="() => handleDownload(DOCUMENT_PDF)"
								>
									<DownloadIcon class="mr-1" />
									Download PDF
								</a>
							</MenuItem>
							<MenuItem v-slot="{ active }">
								<a
									:class="[active ? 'bg-won-sand-light' : '']"
									class="
										flex cursor-pointer items-center justify-start px-2 py-1 text-[12px]
										leading-[14px] font-normal text-won-blue
									"
									@click="duplicateResume"
								>
									<svg
										class="mr-1 size-4 stroke-2 text-won-blue"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									> <g clip-path="url(#clip0_1_8267)"> <path
										d="M10.6666 0.666504H2.66659C1.93325 0.666504 1.33325 1.2665 1.33325 1.99984V11.3332H2.66659V1.99984H10.6666V0.666504ZM12.6666 3.33317H5.33325C4.59992 3.33317 3.99992 3.93317 3.99992 4.6665V13.9998C3.99992 14.7332 4.59992 15.3332 5.33325 15.3332H12.6666C13.3999 15.3332 13.9999 14.7332 13.9999 13.9998V4.6665C13.9999 3.93317 13.3999 3.33317 12.6666 3.33317ZM12.6666 13.9998H5.33325V4.6665H12.6666V13.9998Z"
										fill="#073B5D"
									/> </g> <defs> <clipPath id="clip0_1_8267"> <rect
										width="16"
										height="16"
										fill="white"
									/> </clipPath> </defs> </svg>
									Duplicate
								</a>
							</MenuItem>
							<MenuItem v-slot="{ active }">   
								<a
									:class="[active ? 'bg-won-sand-light' : '']"
									class="
										flex cursor-pointer items-center justify-start px-2 py-1
										text-[12px] leading-[14px] font-normal text-won-blue
									"
									@click="renameResume"
								>
									<svg
										class="mr-1 size-4 stroke-2 text-won-blue"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									> <g clip-path="url(#clip0_1_8270)"> <path
										d="M12.2733 3.86659L11.4667 3.05992C10.9467 2.53992 10.1 2.53992 9.58 3.05992L7.79333 4.84659L2 10.6399V13.3333H4.69333L10.52 7.50659L12.2733 5.75326C12.8 5.23326 12.8 4.38659 12.2733 3.86659ZM4.14 11.9999H3.33333V11.1933L9.10667 5.41992L9.91333 6.22659L4.14 11.9999ZM7.33333 13.3333L10 10.6666H14V13.3333H7.33333Z"
										fill="#073B5D"
									/> </g> <defs> <clipPath id="clip0_1_8270"> <rect
										width="16"
										height="16"
										fill="white"
									/> </clipPath> </defs> </svg>
									Rename
								</a>
							</MenuItem>
							<MenuItem v-slot="{ active }">   
								<a
									:class="[active ? 'bg-won-sand-light' : '']"
									class="
										delete-resume-btn flex cursor-pointer items-center justify-start px-2
										py-1 text-[12px] leading-[14px] font-normal text-won-blue
									"
									@click="deleteResume"
								>
									<svg
										class="mr-1 size-4 stroke-2 text-won-blue"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									> <g clip-path="url(#clip0_1_8273)"> <path
										d="M3.99992 12.6667C3.99992 13.4 4.59992 14 5.33325 14H10.6666C11.3999 14 11.9999 13.4 11.9999 12.6667V4.66667H3.99992V12.6667ZM12.6666 2.66667H10.3333L9.66659 2H6.33325L5.66659 2.66667H3.33325V4H12.6666V2.66667Z"
										fill="#073B5D"
									/> </g> <defs> <clipPath id="clip0_1_8273"> <rect
										width="16"
										height="16"
										fill="white"
									/> </clipPath> </defs> </svg>
									Delete
								</a>
							</MenuItem>
						</div>
					</MenuItems> 
				</transition>
			</Menu>
		</div>

		<delete-modal
			:key="deleteVisible"
			:visible="deleteVisible"
			:title="`Delete \`${title || 'Title'}\``"
			:loading="loadingDelete"
			subtitle="Are you sure you want to delete this resume?"
			@delete-modal:close="handleCloseDelete"
			@delete-modal:confirm="handleDeleteConfirm"
		/>
	</div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { UserGroupIcon } from '@heroicons/vue/24/outline';
import { extractContent } from '../../utils/helpers';

import { useBuilder } from '../../store/builder.store';
import { useOnboardingStore } from '../../store/onboarding.store';
import { useNotifications } from '../../store/notifications.store';
// TODO: REVERT - Restore useServicesNudges import when builder-entry nudge experiment is reverted
// import { useServicesNudges } from '../../store/services-nudges.store';
import { useResumesStore } from '../../store/resumes.store.js';
import { useCoverLettersStore } from '../../store/cover-letters.store';
import { useResumeShare } from '../../store/share-resume.store';
import { setAuthState } from '../../composables/auth';

import {
	createResume
} from '../../api/builder';
import {
	createCoverLetter, retrieveCoverLetter
} from '../../api/cover-letters';

import DeleteModal from '../../modals/delete-modal.vue';
import { DOCUMENT_PDF } from '../../store/builder-style.store';

import ArrowBack from '../../icons/arrow-back';
import SavedResume from '../../icons/pdf-saved';
import DownloadIcon from '../../icons/download';

const emit = defineEmits(['download']);

const router = useRouter();
const route = useRoute();
const { userHaveAccessTo } = setAuthState();

const builder = useBuilder();
builder.activeRoute = useRoute();

const resumesStore = useResumesStore();
const coverLettersStore = useCoverLettersStore();
const resumeShareStore = useResumeShare();
const notificationsStore = useNotifications();

// TODO: REVERT - Restore servicesNudgesStore when builder-entry nudge experiment is reverted
// const servicesNudgesStore = useServicesNudges();

const { meta: { coverLetter = false } } = useRoute();

const onboardingStore = useOnboardingStore();
const { isOnboarding, onboardingStep } = storeToRefs(onboardingStore);

const { data, saved, loaded } = storeToRefs(builder);

const notificationStore = useNotifications();

const title = ref('');
const resumeInput = ref('');

const deleteVisible = ref(false);
const loadingDelete = ref(false);
const loadingDownload = ref(false);

watch(loaded, () => {
	if (loaded.value) {
		title.value = extractContent(data.value.title);
	}
});

watch(
	() => route.params.id,
	async (newId) => {
		if (newId) {builder.init(newId, router, route);}
	}
);

const handleShare = () => {
	resumeShareStore.setShowShareModal();
};

const handleDownload = async (type) => {
	// TODO: REVERT - Re-enable resume nudge on download when builder-entry nudge experiment is reverted
	// await servicesNudgesStore.showNudgeModalFor(servicesNudgesStore.FEATURES.resumeAi);

	loadingDownload.value = true;

	await builder.download(null, type);

	notificationStore.notify('Your download is ready!');
	loadingDownload.value = false;

	emit('download', { type });  
};

const duplicateResume = async() => {
	const result = coverLetter
		? await retrieveCoverLetter(route.params.id)
		: builder.data;
    
	const parsed = builder.buildData(result, coverLetter);

	const duplicatedData = {
		...parsed,
		title: `${parsed.title  } - Copy`,
		id: undefined,
	};

	if (coverLetter) {
		createCoverLetter(duplicatedData).then((res) => {
			router.push({
				name: 'cover-letters.edit',
				params: { id: res.uuid  }
			});
		});
	} else {
		createResume(duplicatedData).then((res) => {
			router.push({
				name: 'resumes.edit',
				params: { id: res.uuid  }
			});
		});
	}
};

const renameResume = () => {
	setTimeout(() => resumeInput.value.focus(), 100);
};

const deleteResume = () => {
	deleteVisible.value = true;
};

const handleDeleteConfirm = async () => {
	loadingDelete.value = true;

	try {
		if (coverLetter) {
			await coverLettersStore.deleteCoverLetter(route.params.id);
		} else {
			await resumesStore.deleteResume(route.params.id);
		}

		router.push({ name: 'home' });
	} catch {
		notificationsStore.notify(`Failed to delete ${coverLetter ? 'cover letter' : 'resume'}!`, 'error');
	} finally {
		handleCloseDelete();
		loadingDelete.value = false;
	}
};

const handleCloseDelete = () => {
	deleteVisible.value = false;
};

const handleBack = () => {
	router.back();
};

const handleTitle = () => {
	builder.changeTitle(title);
};

const selectContent = (e) => {
	e.target.select();
};
</script>

<style scoped>
.pdf-input {
  background-color: #363f4b;
  border-radius: 6px;

  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: #ffffff;

  outline: none !important;
}
</style>
